import { PirBoxActivityProductDetail, PirBoxProductsDetails } from '../../model/pirbox/pir-box-product-detail';
import {
  PirBoxPosition,
  PirBoxProductDetail,
  PirBoxProductSummary,
} from '../../model/pirbox/pirbox-product-summary';
import { PirTypesEnumReverse } from '../../store';
import {
  MovimentoPIR,
  PosizionePIR,
  VisualizzaDettaglioProdottiResponse,
} from '../generated/anagrafica';
import {
  PirDepositDetailBean,
  PirDetailBean,
  PirMovementBean,
  PirPositionBean,
} from '../generated/objectway-zenith';
import { parseServerAmount } from './commons-api-parsers';

export const parseTableDetailProductPirBoxPosition = (
  tab: VisualizzaDettaglioProdottiResponse,
  codMandatoPir: string
) => {
  return {
    positions: tab.posizioni.map((product) =>
      parseTableDetailProductPirBox(product)
    ),
    codMandatoPir: codMandatoPir,
  };
};

export const parseTableDetailProductPirBox = (tab: PosizionePIR) => {
  return {
    codMandatoPir: tab.codMandatoPir,
    codPirPlain: tab.codPianoPir,
    codTitle: tab.codTitolo,
    description: tab.desProdotto,
    codMandato: tab.codMandato,
    totalAmountInvested: numberToAmount(tab.importoTotInvestito),
    totalAmountDivested: numberToAmount(tab.importoTotDisinvestito),
    commitment: numberToAmount(tab.importoCommittment),
    totalCMP: numberToAmount(tab.CMPComplessivo),
    concentrationPercentage: tab.percentualeConcentrazione,
    concentrationLimitExceeded: tab.flagLimiteConcentrazioneSuperato,
    flagCompilant: tab.flagCompliant,
    activity: tab.movimenti.map((product) =>
      parseDetailProductPirBoxActivity(product)
    ),
  };
};

export const parseDetailProductPirBoxActivity = (tab: MovimentoPIR) => {
  return {
    code: `${tab.codDivisa}-${tab.numQuote}`,
    amountInvested: numberToAmount(tab.importoInvestito),
    amountDivested: numberToAmount(tab.importoDisinvestito),
    numQuotes: tab.numQuote,
    currency: tab.codDivisa,
    dateStartHoldingPeriod: new Date(tab.dataInizioHoldingPeriod),
    dateEndHHoldingPeriod: new Date(tab.dataFineHoldingPeriod),
    dateRegulation: new Date(tab.dataRegolamento),
  };
};

export const parsePirPlan = (
  pirPlan: PirDepositDetailBean
): PirBoxProductSummary => {
  return {
    customerCode: pirPlan.customerCode,
    pirType: PirTypesEnumReverse[pirPlan.pirType],
    pirCode: pirPlan.pirCode,
    totalCommitment: numberToAmount(pirPlan.commitment),
    productsDetails: parsePirBoxDetailMap(pirPlan.pirDetailMap),
    totalDivested: numberToAmount(pirPlan.totalSell),
    totalInvested: numberToAmount(pirPlan.totalBuy),
    openingDate: parseDate(pirPlan.pirOpenDate),
    closingDate: parseDate(pirPlan.pirClosingDate),
    status: pirPlan.status,
    lostBenefitDate: parseDate(pirPlan.lostBenefitDate),
    maxAnnualInvestable: numberToAmount(pirPlan.yearlyMaxAmount),
    totalInvestable: numberToAmount(pirPlan.maxAmount),
    totalNet: numberToAmount(pirPlan.totalInvestment),
    annualInvestableResidue: numberToAmount(pirPlan.yearlyResidualAmount),
    totalInvestableResidue: numberToAmount(pirPlan.residualAmount),
  };
};


export function parsePirPlanDetails(result: { [key: string]: PirPositionBean; }): PirBoxProductsDetails[]  {
  return Object.entries(result).flatMap((entry)=>parsePirPlanDetailPosition(entry[1]))
}

function parseDate(date: string): Date {
  return date !== null ? new Date(date) : undefined;
}

function parsePirBoxDetailMap(pirDetailMap: {
  [key: string]: PirDetailBean;
}): PirBoxProductDetail[] {
  const productDetails: PirBoxProductDetail[] = [];
  const totalPositions = Object.values(pirDetailMap)
    .map((detail) => detail.positions.length)
    .reduce((accumulator, currentLength) => accumulator + currentLength, 0);
  for (const typology of Object.keys(pirDetailMap)) {
    productDetails.push(
      parsePirDetailBean(typology, totalPositions, pirDetailMap[typology])
    );
  }
  return productDetails;
}
function parsePirDetailBean(
  typology: string,
  totalPositions: number,
  detail: PirDetailBean
): PirBoxProductDetail {
  const positionLength = detail.positions.length;
  return {
    typology,
    percentage: (positionLength / totalPositions) * 100,
    totalInvestment: numberToAmount(detail.totalInvestment), //yearlyMaxAmount
    annualDivested: numberToAmount(detail.totalSell), //totalSell ?
    annualInvested: numberToAmount(detail.totalBuy), //totalBuy ?
    weight: detail.weight,
    positions: Object.values(detail.positions).map((position) =>
      parseDetailPositions(position)
    ),
  };
}

const numberToAmount = (n: number) => {
  return parseServerAmount({
    valore: n,
  });
};

function parseDetailPositions(positions: PirPositionBean): PirBoxPosition {
  return {
    titleCode: positions.productCode,
    description: positions.productName,
    compliant: positions.compliant,
    committment: numberToAmount(positions.commitment),
    totalDivested: numberToAmount(positions.totalSell),
    totalInvested: numberToAmount(positions.totalBuy),
    concentration: positions.weight,
  };
}
function parsePirPlanDetailPosition(positionBean: PirPositionBean): PirBoxProductsDetails {
  return {
    codTitle: positionBean.productCode,
    description: positionBean.productName,
    codMandato: positionBean.contractCode,
    totalAmountInvested: numberToAmount(positionBean.totalBuy),
    totalAmountDivested: numberToAmount(positionBean.totalSell),
    totalQuantity: positionBean.quantity,
    commitment: numberToAmount(positionBean.commitment),
    totalCMP: numberToAmount(positionBean.cmp),
    flagCompilant: positionBean.compliant,
    activity: positionBean.movements.map((movement)=>parsePlanDetailMovement(movement))
  }; 
}

function parsePlanDetailMovement(movement: PirMovementBean): PirBoxActivityProductDetail {
  return {
    code: movement.code,
    amountInvested: numberToAmount(movement.invested),
    amountDivested: numberToAmount(movement.disinvested),
    numQuotes: movement.quantity,
    currency: movement.currency,
    dateStartHoldingPeriod: parseDate(movement.movementDate),
    dateEndHHoldingPeriod: parseDate(movement.holdingPeriod),
    dateRegulation: new Date()
  }
}

